import { isDefined } from "@clipboard-health/util-ts";
import { ButtonInternalLink } from "@src/appV2/redesign/components/ButtonInternalLink";
import {
  CollapsibleBlock,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Text,
} from "@src/appV2/redesign/system/ui-components";
import { useState } from "react";

const NEVER_NO_SHOW_SECTION_IDS = {
  WHEN_CONSIDERED: "When am I considered a No Show?",
  AVOID_NO_SHOWS: "How can I avoid No Shows?",
  UPDATE_ETA: "Where can I update my ETA?",
  LOST_SHIFT: "I updated my ETA, why did I still lose my shift?",
  CANT_MAKE_IT: "I can't make it, what do I do?",
} as const;

type NeverNoShowSectionId =
  (typeof NEVER_NO_SHOW_SECTION_IDS)[keyof typeof NEVER_NO_SHOW_SECTION_IDS];

interface NeverNoShowSection {
  id: NeverNoShowSectionId;
  title: string;
  intro: string;
  bullets: string[];
}

function getSections(cancelBeforeShiftStartPointDeduction?: number): NeverNoShowSection[] {
  return [
    {
      id: NEVER_NO_SHOW_SECTION_IDS.WHEN_CONSIDERED,
      title: NEVER_NO_SHOW_SECTION_IDS.WHEN_CONSIDERED,
      intro:
        "If you are on time for your shift, you will never be considered a No Show. If you are running late, then one of the following can happen:",
      bullets: [
        "Workplace might mark you as a No Show if your projected arrival time is too late for them.",
        "Clipboard will mark you as a No Show if you are very late and not moving towards the workplace.",
      ],
    },
    {
      id: NEVER_NO_SHOW_SECTION_IDS.AVOID_NO_SHOWS,
      title: NEVER_NO_SHOW_SECTION_IDS.AVOID_NO_SHOWS,
      intro:
        "If you will be arriving after the shift start time, you should always do the following.",
      bullets: [
        "Share a new ETA with Clipboard before leaving home. This will prevent Clipboard from automatically marking you as a No Show.",
        "Keep location tracking on so that the workplace sees you are moving.",
      ],
    },
    {
      id: NEVER_NO_SHOW_SECTION_IDS.UPDATE_ETA,
      title: NEVER_NO_SHOW_SECTION_IDS.UPDATE_ETA,
      intro:
        "You must update your ETA in app! It's not enough to message the workplace as Clipboard will not receive your message.",
      bullets: [
        "Navigate to My Bookings and tap on your upcoming shift.",
        'Tap the "Update ETA" button.',
        "Select an ETA which you can make.",
      ],
    },
    {
      id: NEVER_NO_SHOW_SECTION_IDS.LOST_SHIFT,
      title: NEVER_NO_SHOW_SECTION_IDS.LOST_SHIFT,
      intro: "One of the following could have happened.",
      bullets: [
        "You started running late relative to the ETA update you gave.",
        "The workplace decided your arrival time was too late for them.",
      ],
    },
    {
      id: NEVER_NO_SHOW_SECTION_IDS.CANT_MAKE_IT,
      title: NEVER_NO_SHOW_SECTION_IDS.CANT_MAKE_IT,
      intro:
        "Always cancel as soon as possible! The only way to protect your account is to cancel before the shift start. After you cancel, you should reach out to our support team as you may be eligible for a one time pass.",
      bullets: [
        "Cancel after shift start time = Instant Restriction.",
        ...(isDefined(cancelBeforeShiftStartPointDeduction)
          ? [
              `Cancel before shift start time = Up to ${cancelBeforeShiftStartPointDeduction} point loss.`,
            ]
          : []),
      ],
    },
  ];
}

interface ClipboardScoreNeverNoShowContentProps {
  cancelBeforeShiftStartPointDeduction?: number;
}

export function ClipboardScoreNeverNoShowContent(props: ClipboardScoreNeverNoShowContentProps) {
  const { cancelBeforeShiftStartPointDeduction } = props;

  const sections = getSections(cancelBeforeShiftStartPointDeduction);
  const [expandedSectionIds, setExpandedSectionIds] = useState<NeverNoShowSectionId[]>([]);

  return (
    <List sx={{ paddingBottom: 11 }}>
      {sections.map((section) => (
        <ListItem key={section.id} divider sx={{ display: "block" }}>
          <CollapsibleBlock
            title={
              <Text semibold variant="h6" sx={{ lineHeight: 1.5, paddingY: 3 }}>
                {section.title}
              </Text>
            }
            LinkComponent={ButtonInternalLink}
            expanded={expandedSectionIds.includes(section.id)}
            onChange={() => {
              if (expandedSectionIds.includes(section.id)) {
                setExpandedSectionIds(
                  expandedSectionIds.filter((expandedId) => expandedId !== section.id)
                );
                return;
              }

              setExpandedSectionIds([...expandedSectionIds, section.id]);
            }}
          >
            <Text variant="body2">{section.intro}</Text>
            <List disablePadding dense>
              {section.bullets.map((message) => (
                <ListItem key={message}>
                  <ListItemIcon iconType="priority" sx={{ alignSelf: "initial" }} />
                  <ListItemText secondary={<Text variant="body2">{message}</Text>} />
                </ListItem>
              ))}
            </List>
          </CollapsibleBlock>
        </ListItem>
      ))}
    </List>
  );
}
